import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertDialogPortal,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { toast } from "sonner";
import { updateDeliveryStatus } from "../apis";
import { Spinner } from "components";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { deliveryStatusSchema, DeliveryStatusSchema } from "../types/delivery";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useMutation, useQueryClient } from "react-query";

interface DeliveryStatusProps {
    children: React.ReactNode;
    defaultValues?: DeliveryStatusSchema;
    id: string;
    disabled?: boolean;
}

export default function DeliveryStatus({
    children,
    defaultValues,
    id:deliverId,
    disabled = false
}: DeliveryStatusProps) {
    const defaultData =  defaultValues ?  {
        status: defaultValues.status,
        date: defaultValues.date,
        quantity: defaultValues?.quantity
    } : {
        status: '',
        date: new Date().toISOString().split('T')[0], // Default to today's date
        quantity: 0
    }
    const form = useForm<DeliveryStatusSchema>({
        resolver: zodResolver(deliveryStatusSchema),
        defaultValues: defaultData,
    });
    const [open, setOpen] = useState(false);
    const query = useQueryClient();
    const id = useParams().id as string

    const { mutate, isLoading } = useMutation({
        mutationFn: (data: DeliveryStatusSchema) => updateDeliveryStatus(id, deliverId, data),
        onSuccess: () => {
            toast.success("Status updated successfully");
            setOpen(false);
            query.invalidateQueries({ queryKey: ['orderDetails'] });
        },
        onError: () => {
            toast.error("Error updating status");
        }
    })

    const onSubmit = (data: DeliveryStatusSchema) => {        
        mutate({
            status: data.status,
            date: data.date,
            quantity: data?.quantity
        })
    };
    console.log('defaultValues', defaultValues)
    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger disabled={disabled} className=" disabled:opacity-40">{children}</AlertDialogTrigger>
            <AlertDialogPortal>
                {isLoading && <Spinner />}
                <AlertDialogOverlay className="bg-black-900/30" />
                <AlertDialogContent className="max-w-4xl py-5 bg-white-A700 rounded-xl">
                    <AlertDialogHeader className="flex items-start border-b pb-5">
                        <AlertDialogTitle>Update Status</AlertDialogTitle>
                        <AlertDialogDescription>Fill the required details</AlertDialogDescription>
                    </AlertDialogHeader>
                    <Form {...form}>
                        <form className="space-y-6">
                            <div className="rounded-lg">
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name='status'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Change Status</FormLabel>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select status" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="Ready to deliver">Ready to deliver</SelectItem>
                                                        <SelectItem value="Out for delivery">Out for delivery</SelectItem>
                                                        <SelectItem value="Delivered">Delivered</SelectItem>
                                                        <SelectItem value="Pending">Pending</SelectItem>
                                                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage className='text-red-500' />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='date'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel htmlFor={field.name}>Enter the Date</FormLabel>
                                                <FormControl>
                                                    <input
                                                        type="date"
                                                        min={new Date().toISOString().split('T')[0]} // Prevent past dates
                                                        {...field}
                                                        onChange={(e)=> field.onChange(e.target.value)}
                                                        className="border border-gray-300 rounded-md p-2 w-full"
                                                    />
                                                </FormControl>
                                                <FormMessage className='text-red-500' />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </form>
                    </Form>
                    <div className="flex flex-col h-full gap-4">
                        {/* Actions */}
                        <div className="flex items-center justify-between mt-auto">
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setOpen(false);
                                    }}
                                    className="text-destructive hover:text-destructive border rounded-md border-blue-700 text-blue-700"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    disabled={
                                        !form.formState.isValid
                                    }
                                    className="bg-blue-700 rounded-md text-white-A700 border border-blue-700"
                                    onClick={form.handleSubmit(onSubmit)}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                </AlertDialogContent>
            </AlertDialogPortal>
        </AlertDialog>
    );
}
